import { useState, type FormEvent } from "react";
import { ACTIVITY_TYPES, type ActivityType } from "../../shared/activities.ts";
import { toMeters, type Units } from "../lib/format";
import { Icon } from "./Icons";

const LABELS: Record<ActivityType, string> = { walk: "Walk", run: "Run", hike: "Hike", treadmill: "Treadmill", row: "Row", cycle: "Cycle", elliptical: "Elliptical", other: "Other" };
const today = () => new Date().toISOString().slice(0, 10);

export interface NewActivity {
  activity_type: ActivityType;
  distance_meters: number;
  occurred_on: string;
  duration_seconds: number | null;
  notes: string | null;
}

interface Props {
  units: Units;
  onUnits: (u: Units) => void;
  onClose: () => void;
  onSubmit: (a: NewActivity) => Promise<void>;
}

export default function LogSheet({ units, onUnits, onClose, onSubmit }: Props) {
  const [type, setType] = useState<ActivityType>("walk");
  const [dist, setDist] = useState("");
  const [date, setDate] = useState(today());
  const [mins, setMins] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const d = parseFloat(dist);
  const ok = d > 0 && !busy;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!ok) return;
    setBusy(true);
    setError(null);
    try {
      await onSubmit({
        activity_type: type,
        distance_meters: toMeters(d, units),
        occurred_on: date,
        duration_seconds: mins ? parseInt(mins, 10) * 60 : null,
        notes: note.trim() || null,
      });
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  };

  return (
    <>
      <div className="scrim" onClick={onClose} />
      <form className="sheet" onSubmit={submit} aria-label="Log activity">
        <div className="handle" />
        <h2 className="sheet-title">Log activity</h2>
        <div className="chips" role="radiogroup" aria-label="Activity type">
          {ACTIVITY_TYPES.map((t) => (
            <button type="button" key={t} role="radio" aria-checked={type === t} className={type === t ? "chip sel" : "chip"} onClick={() => setType(t)}>
              <Icon id={`act-${t}`} size={19} />
              {LABELS[t]}
            </button>
          ))}
        </div>
        <div className="row first">
          <input className="input" inputMode="decimal" placeholder="Distance" aria-label="Distance" value={dist} onChange={(e) => setDist(e.target.value)} autoFocus />
          <div className="seg" role="radiogroup" aria-label="Units">
            {(["mi", "km"] as Units[]).map((u) => (
              <button type="button" key={u} role="radio" aria-checked={units === u} className={units === u ? "seg-btn narrow sel" : "seg-btn narrow"} onClick={() => onUnits(u)}>{u}</button>
            ))}
          </div>
        </div>
        <div className="row">
          <input className="input wide" type="date" aria-label="Date" value={date} max={today()} onChange={(e) => setDate(e.target.value)} />
          <input className="input" inputMode="numeric" placeholder="Minutes (opt.)" aria-label="Minutes" value={mins} onChange={(e) => setMins(e.target.value.replace(/[^0-9]/g, ""))} />
        </div>
        <input className="input block" placeholder="A note for the road (optional)" aria-label="Note" maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} />
        {error && <p className="error">{error}</p>}
        <button type="submit" className="btn-primary block" style={{ marginTop: 16 }} disabled={!ok}>{busy ? "Saving…" : "Add to the journey"}</button>
      </form>
    </>
  );
}
